import {
  Calendar,
  CalendarOff,
  Store,
  SunMoon,
  TrendingUp,
  User,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * 'primary' sits in the top bar — the handful of things an owner opens daily.
 * The others live behind Settings, grouped by what they're about.
 */
export type NavGroup = 'primary' | 'salon' | 'account';

/**
 * What every NAV row must look like. `id` is a bare string only so that `Tab`
 * can be derived from NAV below without the two referring to each other in a
 * circle; consumers get the narrowed `NavItem`.
 */
type NavRow = {
  id: string;
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Workers only ever get the calendar. */
  ownerOnly: boolean;
};

export const NAV = [
  { id: 'calendar', label: 'Calendar', icon: Calendar, group: 'primary', ownerOnly: false },
  { id: 'clients', label: 'Clients', icon: UserCheck, group: 'primary', ownerOnly: true },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp, group: 'primary', ownerOnly: true },
  { id: 'salon', label: 'Salon', icon: Store, group: 'salon', ownerOnly: true },
  { id: 'staff', label: 'Staff', icon: Users, group: 'salon', ownerOnly: true },
  { id: 'timeoff', label: 'Time Off', icon: CalendarOff, group: 'salon', ownerOnly: true },
  { id: 'account', label: 'Account', icon: User, group: 'account', ownerOnly: true },
  { id: 'appearance', label: 'Appearance', icon: SunMoon, group: 'account', ownerOnly: true },
] as const satisfies readonly NavRow[];

/**
 * Every destination in the owner dashboard. Derived from NAV rather than
 * declared beside it: a tab with no row would otherwise type-check while the
 * bar, the rail and the drawer all quietly failed to render it.
 */
export type Tab = (typeof NAV)[number]['id'];

/** A nav row, with its id narrowed to the tabs that actually exist. */
export type NavItem = NavRow & { id: Tab };

export const PRIMARY_NAV: readonly NavItem[] = NAV.filter((item) => item.group === 'primary');

/** The settings rail, in the order it renders. */
export const SETTINGS_GROUPS: readonly { heading: string; items: readonly NavItem[] }[] = [
  { heading: 'Your salon', items: NAV.filter((item) => item.group === 'salon') },
  { heading: 'Your account', items: NAV.filter((item) => item.group === 'account') },
];

const BY_ID = new Map<string, NavItem>(NAV.map((item) => [item.id, item]));

/** The row behind a tab — its label and icon, wherever they need re-rendering. */
export const navItem = (tab: Tab): NavItem | undefined => BY_ID.get(tab);

/**
 * True when the tab renders inside the settings hub rather than as a top-bar
 * tab. Fails closed on an unknown id: a destination with no row is not a
 * settings tab, and treating it as one would swap the whole nav for "Back to
 * calendar" with nothing behind it.
 */
export const isSettingsTab = (tab: Tab): boolean => {
  const item = BY_ID.get(tab);
  return item !== undefined && item.group !== 'primary';
};

/**
 * Resolve a URL hash to a tab. The hash namespace is shared with the worker
 * invite link (#set-password?email=...), so anything unrecognised falls back to
 * the calendar rather than erroring.
 */
export function tabFromHash(rawHash: string): Tab {
  const hash = rawHash.replace(/^#/, '');
  // Legacy hash: the old single "Settings" tab is now the Salon tab.
  if (hash === 'settings') return 'salon';
  return BY_ID.has(hash) ? (hash as Tab) : 'calendar';
}

/** The calendar is the default view, so it gets a clean URL with no hash. */
export const hashForTab = (tab: Tab): string => (tab === 'calendar' ? '' : tab);
